import "../styles/people.scss";
import { useMemo, useEffect } from "react";
import user from "../actions/user-actions";
import PageLayout from "../components/page-layout";
import actions from "../actions/actions";
import { useIsDesktop } from "../utilities/constants";
import { GuiButton } from "../components/global-ui/gui-button";
import { useSelector } from "react-redux";
import Card from "../components/card";
import { SearchBox } from "../components/search-box";
import {
  downloadFile,
  pluralize,
  useUpdateState,
} from "../utilities/utilities";
import Grid, { ColumnHeader, GridCell } from "../components/grid";
import {
  CheckBoxHeaderButton,
  GuiCheckbox,
} from "../components/global-ui/gui-checkbox";
import Popover from "../components/popover";
import { GuiSelect } from "../components/global-ui/gui-select";
import people from "../actions/people-actions";
import { useOnFirstLoad } from "../utilities/hooks";
import { Modal } from "../components/modal";
import { GuiTooltip } from "../components/global-ui/gui-tooltip";
import { Icon } from "../components/icon";
import { FilterDropdown } from "../components/filter-dropdown";
import { EditMultiple } from "../components/edit-multiple";
import { ColumnPicker } from "../components/column-picker";
import { storage } from "../utilities/storage";
import { dispatch } from "../redux/redux-config";
import { FilterTagBar } from "../components/filter-tag-bar";
import { Tag } from "../components/tag";
import BottomNavBar from "./BottomNavBar";
import { showToast } from "../components/toast";
import { FilterPopup } from "../components/filter-popup";
import { DISCARD_ROUTES } from "../utilities/constants";
import { Path, UserRole } from "../utilities/constants";
import navigate from "../utilities/navigation";

let noResultsToastShown = false;
export default function PeoplePage() {
  useOnFirstLoad(people.initPage);
  useOnFirstLoad(actions.groups.initPage); // Ensure that locationOptions is loaded
  let { locationOptions } = useSelector((store) => store.groups);

  let groupTypeOptions =
    useSelector((store) => actions.groups.loadGroupTypes()) || [];

  let { gridColumns } = useSelector((store) => store.people);
  const currentUser = useSelector((store) => store.user);
  const userRole = currentUser?.userType;
  let groupOptions =
    useSelector((store) => actions.groups.loadGroupOptions()) || [];
  // Use local tate for userTypeOptions loaded from API
  let [userTypeOptions, setUserTypeOptions] = useUpdateState([]);
  let isMobile = useSelector((store) => store.page.isMobile);
  let [checkboxState, updateCheckboxState, setCheckboxState] = useUpdateState(
    {},
  );
  let [state, updateState] = useUpdateState(() => {
    return {
      criteriaOptions: [
        { value: "no-login", label: "Never Logged In" },
        { value: "no-email", label: "No Email" },
        { value: "no-cell", label: "No Cell Number" },
        { value: "no-phone", label: "No Phone Number" },
        { value: "no-phone-or-email", label: "No Phone Number or Email" },
        { value: "one-phone", label: "Only One Phone Number" },
        { value: "has-phone", label: "Has a Phone Number" },
        { value: "no-import-delete", label: "Do Not Delete On Import" },
        { value: "alert-permission", label: "Has Message Permissions" },
        { value: "opted-out-sms", label: "Opted Out of Text Messages" },
      ],
      editMultipleItems: [
        { name: "Add Users to Group" },
        { name: "Archive Users" },
        { name: "Send Message to Users" },
        { name: "Make High Priority" },
        { name: "Remove High Priority" },
        { name: "Export" },
      ],
      gridReset: 0,
      search: "",
      filters: [],
      sort: { name: "asc" },
      perms: {},
      isDeleteEnabled: false,
    };
  });

  let [filterState, setFilterState] = useUpdateState({
    locationId: null,
    groupType: null,
    filteredGroups: groupOptions,
  });

  let userTypeArray = Array.isArray(userTypeOptions)
    ? userTypeOptions
    : Object.values(userTypeOptions).map((ut) => ({
        value: ut?.id,
        label: ut?.name,
      }));

  let filterOptions = [
    { name: "User Type", options: userTypeArray || [] },
    { name: "Group", options: groupOptions || [] },
    { name: "Criteria", options: state?.criteriaOptions || [] },
  ];

  let selectedRows = Object.keys(checkboxState).filter((k) => checkboxState[k]);
  let { sort, perms } = state;
  let disabled = selectedRows.length === 0;
  gridColumns = gridColumns || [];
  let visibility = useMemo(() => {
    let vis = {};
    gridColumns.forEach((c) => (vis[c.column] = c.show));
    return vis;
  }, [gridColumns]);

  async function fetchUserTypes() {
    let result = await user.getUserTypeDropdown();
    if (result && result.success && Array.isArray(result.list)) {
      setUserTypeOptions(result.list);
    }
  }

  useEffect(() => {
    fetchUserTypes();
  }, []);

  async function loadRows(page, pageSize, skipTotal) {
    let search = state.search ? state.search.trim() : "";
    let userTypes = state.filters
      .filter((f) => f.name === "User Type")
      .map((f) => f.value);
    let userType =
      userTypes.length > 1 ? -1 : userTypes.length === 1 ? userTypes[0] : null;
    let groups = state.filters
      .filter((f) => f.name === "Group")
      .map((f) => f.value);
    let criterias = state.filters
      .filter((f) => f.name === "Criteria")
      .map((f) => f.value);
    let sort = Object.keys(state.sort)
      .map((col) => ({ column: col, asc: state.sort[col] }))
      .filter((s) => s.asc !== undefined)
      .map((s) => s.column + " " + (s.asc ? "asc" : "desc"))
      .join(",");

    // Only show loading state for full reloads (page 1, search/filter change).
    // Pre-loads (skipTotal=true, page 2+) happen silently so the SearchBox
    // doesn't flicker and the grid doesn't re-show its spinner.
    if (!skipTotal) updateState({ loading: true });

    try {
      let result = await actions.people.getPeopleGrid(
        search,
        userType,
        groups,
        criterias,
        sort,
        page,
        pageSize,
        skipTotal,
      );

      let safeList = Array.isArray(result?.list) ? result.list : [];
      let changes = {
        loading: false,
        currentPage: safeList,
        latestPage: safeList,
      };

      if (safeList.length === 0) {
        if (!noResultsToastShown) {
          showToast("error", "No results found");
          noResultsToastShown = true;
          setTimeout(() => {
            noResultsToastShown = false;
          }, 10000); // Reset after 10s
        }
      } else {
        noResultsToastShown = false; // Reset if results found
      }

      if (result?.permissions) {
        changes.perms = result.permissions;
        if (!result.permissions.canExport)
          changes.editMultipleItems = state.editMultipleItems.filter(
            (i) => i.name !== "Export",
          );
        if (result.permissions.deleteLevel <= 0)
          changes.editMultipleItems = state.editMultipleItems.filter(
            (i) => i.name !== "Delete Users from System",
          );
      }

      updateState(changes);
      return {
        ...result,
        list: safeList,
        totalCount: Number.isFinite(result?.totalCount) ? result.totalCount : 0,
      };
    } catch (error) {
      console.error("Failed to load people grid", error);
      updateState({ loading: false, currentPage: [], latestPage: [] });
      return { list: [], totalCount: 0, permissions: null };
    }
  }

  function setSearch(search) {
    updateState({ search: search, gridReset: state.gridReset + 1 });
  }

  function onFilterChange(action, values) {
    if (action === "clear")
      updateState({
        filters: [],
        gridReset: state.gridReset + 1,
        selectedValues: values,
      });
    else {
      let newFilters = state.filters.concat(values);
      updateState({
        filters: newFilters,
        gridReset: state.gridReset + 1,
        selectedValues: values,
      });
    }
  }
  function onFilterChangeMobile(action, values) {
    if (action === "clear")
      updateState({
        filters: [],
        gridReset: state.gridReset + 1,
        selectedValues: values,
      });
    else {
      updateState({
        filters: values,
        gridReset: state.gridReset + 1,
        selectedValues: values,
      });
    }
  }

  function removeFilter(filter) {
    updateState({
      filters: state.filters.filter((f) => f !== filter),
      gridReset: state.gridReset + 1,
    });
  }

  function onSort(column) {
    let currentDirection = state.sort[column];
    let nextDirection =
      currentDirection === true
        ? false
        : currentDirection === false
          ? undefined
          : true;
    updateState({
      sort: { ...state.sort, [column]: nextDirection },
      gridReset: state.gridReset + 1,
    });
  }

  function onPageChanged(list) {
    setCheckboxState({});
    updateState({ currentPage: list });
  }

  async function onEditMultipleClick(item) {
    if (item.name === "Add Users to Group")
      updateState({ showAddToGroupModal: true, individual: null });
    else if (item.name === "Archive Users")
      updateState({ showDeleteUsersModal: true, individual: null });
    else if (item.name === "Send Message to Users") goToSendAlert();
    else if (item.name === "Make High Priority")
      updateState({ showMakeHighPriority: true, individual: null });
    else if (item.name === "Remove High Priority")
      updateState({ showRemoveHighPriority: true, individual: null });
    else if (item.name === "Export") {
      let token = await actions.user.getShortTermToken();
      if (token) {
        downloadFile(
          "/api/people/export?personIds=" +
            Object.keys(checkboxState)
              .filter((k) => checkboxState[k])
              .join(",") +
            "&token=" +
            encodeURIComponent(token),
        );
      }
    }
  }

  function onColumnChange(item, show) {
    let cols = [...gridColumns];
    let i = cols.findIndex((c) => c.name === item.name);
    cols[i] = { ...cols[i], show: show };

    let list = cols.filter((c) => c.show).map((c) => c.column);
    storage.setObject("people-columns", list);

    dispatch("PEOPLE_SETTINGS_UPDATE", { gridColumns: cols });
  }

  function toggleAllCheckboxes() {
    let any = Object.keys(checkboxState).find((k) => checkboxState[k]);
    if (any || !state.currentPage) {
      setCheckboxState({});
      updateState({ isDeleteEnabled: false });
    } else {
      setCheckboxState(
        state.currentPage.reduce((newState, row) => {
          newState[row.id] = true;
          return newState;
        }, {}),
      );
      updateState({ isDeleteEnabled: true });
    }
  }

  function getUsersText() {
    return state?.individual ? state?.individual?.name : "these users";
  }

  async function addUsersToGroup() {
    try {
      const selectedUserIds = Object.keys(checkboxState).filter(
        (k) => checkboxState[k],
      );
      const userIds =
        selectedUserIds.length > 0
          ? selectedUserIds
          : state?.individual
            ? [state?.individual?.id]
            : [];

      updateState({ saving: true });

      const result = await actions.people.addPeopleToGroup(
        state.addToGroupId,
        userIds,
      );

      if (result?.success) {
        showToast("success", "User added to group successfully!");
        setCheckboxState({});
        updateState({
          saving: false,
          showAddToGroupModal: false,
          addToGroupId: null,
          individual: null,
        });
      } else {
        const errorMsg =
          result?.errorMessage && result.errorMessage.trim().length > 0
            ? result.errorMessage
            : "Failed to add user to group.";
        showToast("error", errorMsg);
        updateState({ saving: false });
        return;
      }
    } catch (err) {
      const errorMsg =
        err?.message && err.message.trim().length > 0
          ? err.message
          : "An unexpected error occurred.";
      showToast("error", errorMsg);
      updateState({ saving: false });
    }
  }

  async function deleteUsers() {
    let userIds = state?.individual
      ? [state?.individual?.id]
      : Object.keys(checkboxState).filter((k) => checkboxState[k]);
    updateState({ saving: true });
    let response = await actions.people.deletePeople(userIds);
    if (response?.success) {
      showToast("success", "User(s) deleted successfully");
    } else {
      showToast("error", "Failed to delete user(s).");
    }
    updateState({
      saving: false,
      showDeleteUsersModal: false,
      individual: null,
      gridReset: state.gridReset + 1,
    });
  }

  async function setUserPriority(setHigh) {
    let userIds = state.individual
      ? [state.individual.id]
      : Object.keys(checkboxState).filter((k) => checkboxState[k]);
    updateState({ saving: true });
    await actions.people.setPriority(userIds, setHigh);
    updateState({
      saving: false,
      showMakeHighPriority: false,
      showRemoveHighPriority: false,
      individual: null,
      gridReset: state.gridReset + 1,
    });
  }

  async function goToSendAlert(userId) {
    let userIds =
      selectedRows && selectedRows.length > 0 ? selectedRows : [userId];
    let userIdsAsNumbers = userIds.map((id) => parseInt(id, 10));
    let users = state.currentPage.filter((p) =>
      userIdsAsNumbers.includes(p.id),
    );

    const firstNames = users.map((user) => user.name.split(" ")[0].trim());

    const groupName = `${firstNames.join(", ")}`;

    //create group based on users :
    let groupData = {
      id: 0,
      name: groupName,
      description: groupName,
      groupCode: "",
      groupType: 5,
      allowOptIn: true,
      locationId: 0,
      alwaysAttach: 0,
      callerId: "",
      emergencyCallerId: "",
      groupEmail: "",
      groupEmailName: "",
      reportingEmail: "",
      reportingEmailName: "",
      logoUrl: "",
      users: users,
      is_temp: 1,
    };
    let result = await actions.groups.save(groupData);
    if (result && result?.success) {
      groupData.id = result?.id;
      await actions.people.addPeopleToGroup(result?.id, userIdsAsNumbers);
      let group = {
        id: result.id,
        name: groupData.name,
        location: groupData.locationId,
        description: groupData.description,
        groupType: groupData.groupType,
        groupTypeName: "",
        groupCode: groupData.groupCode,
        createdUserId: 0,
        userCount: groupData.users.count,
        alertCount: 0,
        is_temp: 1,
      };
      let groupArray = [group];
      actions.alerts.showCreateAlert(
        false,
        groupArray,
        null,
        DISCARD_ROUTES.people,
      );
    }
  }

  async function exportAll() {
    let token = await actions.user.getShortTermToken();
    if (token) {
      let search = state.search;
      let userTypes = state.filters
        .filter((f) => f.name === "User Type")
        .map((f) => f.value);
      let userType =
        userTypes.length > 1
          ? -1
          : userTypes.length === 1
            ? userTypes[0]
            : null;
      let groups = state.filters
        .filter((f) => f.name === "Group")
        .map((f) => f.value);
      let criterias = state.filters
        .filter((f) => f.name === "Criteria")
        .map((f) => f.value);
      let sort = Object.keys(state.sort)
        .map((col) => ({ column: col, asc: state.sort[col] }))
        .filter((s) => s.asc !== undefined)
        .map((s) => s.column + " " + (s.asc ? "asc" : "desc"))
        .join(",");

      downloadFile(
        "/api/people/export?search=" +
          encodeURIComponent(search) +
          "&userType=" +
          (userType || "") +
          "&groups=" +
          groups.join(",") +
          "&criterias=" +
          criterias.join(",") +
          "&sort=" +
          (sort || "") +
          "&token=" +
          encodeURIComponent(token),
      );
    }
  }

  function getUserType(userType) {
    if (!userTypeOptions) return "";
    const arr = Array.isArray(userTypeOptions)
      ? userTypeOptions
      : Object.values(userTypeOptions);
    const found = arr.find(
      (ut) =>
        String(ut.id) === String(userType) ||
        Number(ut.id) === Number(userType),
    );
    return found && found.name ? found.name : "";
  }

  function UserTypeTag({ user }) {
    let label = getUserType(user);
    return label ? (
      <span
        className={`people-role-badge role-${user}`}
        onClick={(e) => e.preventDefault()}
      >
        {label}
      </span>
    ) : null;
  }
  const handleKeyDown = (e) => {};

  function getInitials(name) {
    const cleanName = (name || "")
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!cleanName) return null;
    return cleanName
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }

  function onTagFilterChange(name, option) {
    let newFilters = state.filters.filter((f) => f.name !== "User Type");
    if (option && option.value) {
      newFilters = [
        ...newFilters,
        { name: "User Type", value: option.value, label: option.label },
      ];
    }
    updateState({ filters: newFilters, gridReset: state.gridReset + 1 });
  }

  const closeModal = () => {
    updateState({
      showAddToGroupModal: false,
      addToGroupId: null,
      individual: null,
    });
    setFilterState({
      locationId: null,
      groupType: null,
      filteredGroups: groupOptions,
    });
  };

  return (
    <PageLayout
      title="People"
      className="people"
      buttonBar={
        <div className="add-person-dropdown">
          <GuiButton
            dataQa="add-person-button"
            className=""
            onClick={() => updateState({ showAddMenu: !state.showAddMenu })}
          >
            <Icon icon="plus" weight="400" />
            Add
          </GuiButton>
          <Popover
            show={state.showAddMenu}
            onHide={() => updateState({ showAddMenu: false })}
          >
            <div
              className="menu-item"
              data-qa="add-person-option"
              onClick={() => {
                updateState({ showAddMenu: false });
                actions.people.showAddPerson();
              }}
            >
              <svg
                className="menu-item-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
              <span>Add Person</span>
            </div>
            {/* Hide this options as per requirements - PBI-350149  */}
            {/* <div
              className="menu-item"
              data-qa="import-users-option"
              onClick={() => {
                updateState({ showAddMenu: false });
                actions.people.showImport();
              }}
            >
              <svg
                className="menu-item-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" />
              </svg>
              <span>Import Users</span>
            </div> */}
          </Popover>
        </div>
      }
    >
      <Card>
        {/* Desktop Search bar */}
        {userRole === UserRole.Admin && !isMobile && (
          <div className="page-layout-content-header">
            <SearchBox
              onSearchChange={setSearch}
              searching={state.loading}
              minLength={3}
              dataQa="search"
            />

            <FilterDropdown
              filters={filterOptions}
              onFilterChange={onFilterChange}
              selectedValues={state?.selectedValues}
            />

            <div className="tools">
              <EditMultiple
                items={state?.editMultipleItems}
                onItemClick={onEditMultipleClick}
                count={selectedRows.length}
              />
              <ColumnPicker items={gridColumns} onItemChange={onColumnChange} />
            </div>

            {/* Filter Tags */}
            <FilterTagBar filters={state?.filters} onRemove={removeFilter} />
          </div>
        )}

        {/* Mobile sub nav */}
        {userRole === UserRole.Admin && isMobile && (
          <>
            {/* Sub Nav: search + filter + controls */}
            <div className="people-sub-nav">
              <div className="people-sub-nav-search-row">
                <SearchBox
                  onSearchChange={setSearch}
                  searching={state.loading}
                  minLength={3}
                />
                <GuiButton
                  className="people-sub-nav-filter-btn"
                  onClick={() => updateState({ isFilterIconClicked: true })}
                >
                  <i className="fa fa-filter"></i>
                  {state.filters.length > 0 && (
                    <span className="people-sub-nav-filter-count">
                      {state.filters.length}
                    </span>
                  )}
                </GuiButton>
                {state.isFilterIconClicked && (
                  <FilterPopup
                    filters={filterOptions}
                    selectedValues={state.selectedValues}
                    onClose={() => updateState({ isFilterIconClicked: false })}
                    onFilterChange={onFilterChangeMobile}
                  />
                )}
              </div>
            </div>

            {/* Selection action bar */}
            <div className="people-selection-bar">
              <div className="people-selection-actions">
                <GuiButton
                  className={"btn-outline" + (disabled ? " disabled" : "")}
                  onClick={goToSendAlert}
                >
                  <i className="far fa-paper-plane"></i> Send Message
                </GuiButton>
                <GuiButton
                  className={"btn-outline" + (disabled ? " disabled" : "")}
                  onClick={() =>
                    updateState({ showAddToGroupModal: true, individual: null })
                  }
                >
                  <i className="fa fa-users"></i> Add to Group
                </GuiButton>
                <GuiButton
                  className={"btn-outline" + (disabled ? " disabled" : "")}
                  onClick={() =>
                    updateState({
                      showActionsMenu: null,
                      showDeleteUsersModal: true,
                    })
                  }
                >
                  <i className="fa fa-trash"></i> Delete
                </GuiButton>
              </div>
            </div>
            <div className="modal-footer-hr-divider"></div>
            <div className="people-selection-bar">
              <div className="people-sub-nav-controls-row">
                <CheckBoxHeaderButton
                  checked={Object.keys(checkboxState).find(
                    (k) => checkboxState[k],
                  )}
                  onClick={toggleAllCheckboxes}
                />
              </div>
              <span className="people-selection-count">
                {selectedRows.length} selected
              </span>{" "}
            </div>
            <div className="modal-footer-hr-divider"></div>
          </>
        )}

        {/* Grid */}
        <Grid
          header={
            <div className="header-row row">
              {userRole === UserRole.Admin && (
                <ColumnHeader className="check">
                  <CheckBoxHeaderButton
                    checked={Object.keys(checkboxState).find(
                      (k) => checkboxState[k],
                    )}
                    onClick={toggleAllCheckboxes}
                  />
                </ColumnHeader>
              )}

              <ColumnHeader
                className="name"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                Name
              </ColumnHeader>

              <ColumnHeader
                className="email"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                Email
              </ColumnHeader>
              <ColumnHeader
                className="cellphone"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                Cell Phone
              </ColumnHeader>
              <ColumnHeader
                className="address"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                Address
              </ColumnHeader>
              <ColumnHeader
                className="phonecode"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                Phone Code
              </ColumnHeader>
              <ColumnHeader
                className="personid"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                Person Id
              </ColumnHeader>
              <ColumnHeader
                className="username"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                User Name
              </ColumnHeader>
              <ColumnHeader
                className="usertype"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                User Type
              </ColumnHeader>
              <ColumnHeader
                className="priority"
                sort={sort}
                onSort={onSort}
                visibility={visibility}
              >
                Priority
              </ColumnHeader>
              {userRole === UserRole.Admin && (
                <ColumnHeader className="send"></ColumnHeader>
              )}

              <ColumnHeader className="actions"></ColumnHeader>
            </div>
          }
          itemTemplate={(p) => (
            <div className="row" key={p.id}>
              {userRole === UserRole.Admin && (
                <GridCell className="check">
                  <GuiCheckbox
                    name={"" + p.id}
                    value={checkboxState[p.id]}
                    onChange={updateCheckboxState}
                  />
                </GridCell>
              )}

              <GridCell className="name" visibility={visibility}>
                <div className="initials">
                  {(() => {
                    // Remove special characters, keep only letters, numbers, and spaces
                    const cleanName = (p.name || "")
                      .replace(/[^a-zA-Z0-9\s]/g, "")
                      .replace(/\s+/g, " ")
                      .trim();

                    if (!cleanName) {
                      // Optionally show a default icon if name is empty
                      return (
                        <i
                          className="fa fa-user"
                          style={{ color: "#ccc", fontSize: "1.2em" }}
                        ></i>
                      );
                    }

                    return cleanName
                      .split(" ")
                      .map((n) => n[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase();
                  })()}
                </div>
                <span className="ml-2">{p.name || ""}</span>
              </GridCell>

              <GridCell className="email" visibility={visibility}>
                {p.email || ""}
              </GridCell>
              <GridCell className="cellphone" visibility={visibility}>
                {p.cellPhone || ""}
              </GridCell>
              <GridCell className="address" visibility={visibility}>
                {p.address || ""} {p.city || ""} {p.state || ""} {p.zip || ""}
              </GridCell>
              <GridCell className="phonecode" visibility={visibility}>
                {p.phoneCode || ""}
              </GridCell>
              <GridCell className="personid" visibility={visibility}>
                {p.uniqueId || ""}
              </GridCell>
              <GridCell className="username" visibility={visibility}>
                {p.userName || ""}
              </GridCell>
              <GridCell className="usertype" visibility={visibility}>
                <UserTypeTag user={p.userTypeId} />
              </GridCell>
              <GridCell className="priority" visibility={visibility}>
                {p.priority && <Tag className="high-priority" label="High" />}
              </GridCell>
              {userRole === UserRole.Admin && (
                <GridCell className="send">
                  <GuiTooltip tooltip="Send Message">
                    <i
                      className="far fa-paper-plane icon-button"
                      onClick={(e) => goToSendAlert(p.id)}
                    />
                  </GuiTooltip>
                </GridCell>
              )}

              {userRole === UserRole.Admin && (
                <GridCell className="actions">
                  <Icon
                    icon="ellipsis-h"
                    weight="400"
                    onClick={(e) => updateState("showActionsMenu", p.id)}
                  />
                  <Popover
                    show={state.showActionsMenu === p.id}
                    className="no-arrow anchor-right popover-menu actions-menu"
                    onHide={() => updateState("showActionsMenu", null)}
                  >
                    <div
                      className="menu-item"
                      onClick={(e) =>
                        updateState({
                          showActionsMenu: null,
                          showAddToGroupModal: true,
                          individual: p,
                        })
                      }
                    >
                      Add User to Group
                    </div>
                    <div
                      className="menu-item"
                      onClick={(e) => goToSendAlert(p.id)}
                    >
                      Send Message
                    </div>
                    {p.priority ? (
                      <div
                        className="menu-item"
                        onClick={(e) =>
                          updateState({
                            showActionsMenu: null,
                            showRemoveHighPriority: true,
                            individual: p,
                          })
                        }
                      >
                        Remove High Priority
                      </div>
                    ) : (
                      <div
                        className="menu-item"
                        onClick={(e) =>
                          updateState({
                            showActionsMenu: null,
                            showMakeHighPriority: true,
                            individual: p,
                          })
                        }
                      >
                        Make High Priority
                      </div>
                    )}
                    <div
                      className="menu-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        localStorage.setItem(
                          "selectedPerson",
                          JSON.stringify({
                            id: p.id,
                            name: p.name.split(" ")[0],
                            userTypeId: p.userTypeId,
                          }),
                        );

                        navigate(Path.PeopleDashboard.replace(":id", p.id));
                      }}
                    >
                      Edit
                    </div>
                    {p.userTypeId <= perms.deleteLevel && (
                      <div
                        className="menu-item"
                        onClick={(e) =>
                          updateState({
                            showActionsMenu: null,
                            showDeleteUsersModal: true,
                            individual: p,
                          })
                        }
                      >
                        Archive
                      </div>
                    )}
                  </Popover>
                </GridCell>
              )}
            </div>
          )}
          mobileItemTemplate={(p) => (
            <div className="row" key={p.id}>
              <div className="people-mobile-row-inner">
                {/* Individual checkbox */}
                {userRole === UserRole.Admin && (
                  <div className="people-mobile-check">
                    <GuiCheckbox
                      name={"" + p.id}
                      value={checkboxState[p.id]}
                      onChange={updateCheckboxState}
                    />
                  </div>
                )}

                {/* Content */}
                <div className="people-mobile-content">
                  <div className="people-mobile-name-row">
                    <span className="people-mobile-name">{p?.name || ""}</span>
                    <UserTypeTag user={p?.userTypeId} />
                  </div>
                  <div className="people-mobile-info">
                    <div className="people-mobile-info-row">
                      <i className="far fa-envelope"></i>
                      <span
                        className={!p.email ? "people-mobile-info-empty" : ""}
                      >
                        {p.email || "—"}
                      </span>
                    </div>
                    <div className="people-mobile-info-row">
                      <i className="fa fa-phone"></i>
                      <span
                        className={
                          !p.cellPhone ? "people-mobile-info-empty" : ""
                        }
                      >
                        {p.cellPhone || "—"}
                      </span>
                    </div>
                    <div className="check-body-details-mobile">
                      <div className="check-body-details-mobile-header">
                        {p.email && p.email.length > 25
                          ? p.email.slice(0, 25) + "..."
                          : p.email || ""}
                      </div>
                      <div className="actions">
                        <Popover
                          show={state.showActionsMenu === p.id}
                          className="no-arrow anchor-right popover-menu actions-menu"
                          onHide={() => updateState("showActionsMenu", null)}
                        >
                          {userRole === UserRole.Admin && (
                            <div
                              className="menu-item"
                              onClick={(e) =>
                                updateState({
                                  showActionsMenu: null,
                                  showAddToGroupModal: true,
                                  individual: p,
                                })
                              }
                            >
                              Add User to Group
                            </div>
                          )}

                          <div
                            className="menu-item"
                            onClick={(e) => goToSendAlert(p?.id)}
                          >
                            Send Message
                          </div>
                          {userRole === UserRole.Admin && (
                            <div
                              className="menu-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                localStorage.setItem(
                                  "selectedPerson",
                                  JSON.stringify({
                                    id: p?.id,
                                    name: p?.name.split(" ")[0],
                                    userTypeId: p?.userTypeId,
                                  }),
                                );
                                navigate(
                                  Path.PeopleDashboard.replace(":id", p?.id),
                                );
                              }}
                            >
                              Edit
                            </div>
                          )}

                          {userRole === UserRole.Admin &&
                            p.userTypeId <= perms.deleteLevel && (
                              <div
                                className="menu-item"
                                onClick={(e) =>
                                  updateState({
                                    showActionsMenu: null,
                                    showDeleteUsersModal: true,
                                    individual: p,
                                  })
                                }
                              >
                                Archive
                              </div>
                            )}
                        </Popover>
                      </div>
                    </div>
                  </div>
                  <div className="tridot">
                    <i
                      className="fa fa-ellipsis-vertical mobile-action-icon"
                      onClick={(e) => updateState("showActionsMenu", p?.id)}
                    ></i>
                  </div>
                </div>

                {/* Ellipsis actions */}
                {userRole === UserRole.Admin && (
                  <div className="people-mobile-row-actions">
                    <Icon
                      icon="ellipsis-h"
                      weight="400"
                      onClick={(e) => {
                        e.stopPropagation();
                        updateState("showActionsMenu", p?.id);
                      }}
                    />
                    <Popover
                      show={state.showActionsMenu === p.id}
                      className="no-arrow anchor-right popover-menu actions-menu"
                      onHide={() => updateState("showActionsMenu", null)}
                    >
                      <div
                        className="menu-item"
                        onClick={() =>
                          updateState({
                            showActionsMenu: null,
                            showAddToGroupModal: true,
                            individual: p,
                          })
                        }
                      >
                        Add User to Group
                      </div>
                      <div
                        className="menu-item"
                        onClick={() => goToSendAlert(p?.id)}
                      >
                        Send Message
                      </div>
                      {p.priority ? (
                        <div
                          className="menu-item"
                          onClick={() =>
                            updateState({
                              showActionsMenu: null,
                              showRemoveHighPriority: true,
                              individual: p,
                            })
                          }
                        >
                          Remove High Priority
                        </div>
                      ) : (
                        <div
                          className="menu-item"
                          onClick={() =>
                            updateState({
                              showActionsMenu: null,
                              showMakeHighPriority: true,
                              individual: p,
                            })
                          }
                        >
                          Make High Priority
                        </div>
                      )}
                      <div
                        className="menu-item"
                        onClick={() => {
                          localStorage.setItem(
                            "selectedPerson",
                            JSON.stringify({
                              id: p?.id,
                              name: p?.name?.split(" ")[0],
                              userTypeId: p?.userTypeId,
                            }),
                          );
                          navigate(Path.PeopleDashboard.replace(":id", p?.id));
                        }}
                      >
                        Edit
                      </div>
                      {p.userTypeId <= perms.deleteLevel && (
                        <div
                          className="menu-item"
                          onClick={() =>
                            updateState({
                              showActionsMenu: null,
                              showDeleteUsersModal: true,
                              individual: p,
                            })
                          }
                        >
                          Delete
                        </div>
                      )}
                    </Popover>
                  </div>
                )}
              </div>
            </div>
          )}
          mobileItemHeight={100}
          reset={state.gridReset}
          pageSize={20}
          onItemsNeeded={loadRows}
          onPageChanged={onPageChanged}
          mobileFooter={(count) =>
            !state.loading
              ? count + " " + pluralize("Person", state?.totalCount)
              : "\u00A0"
          }
        ></Grid>
      </Card>

      {/* Edit */}
      {/* {state.showEditUserModal && (
        <EditPersonModal
          userId={state.individual.id}
          onClose={(changed) =>
            updateState({
              showEditUserModal: false,
              gridReset: state.gridReset + (changed ? 1 : 0),
            })
          }
        />
      )} */}
      {!useIsDesktop() && <BottomNavBar />}
      {/* Add to Group */}
      {state.showAddToGroupModal && (
        <Modal className="plain-modal">
          <p>Select a group you would like to add {getUsersText()} to.</p>
          <Icon icon="times" className="close" onClick={closeModal} />
          <div className="dropdown-group">
            <GuiSelect
              name="locationId"
              label="High School"
              options={locationOptions}
              value={filterState.locationId}
              onChange={(name, option) =>
                setFilterState({ locationId: option.value })
              }
              onKeyDown={(e) => handleKeyDown(e)}
            />

            <GuiSelect
              name="groupType"
              label="Group Type"
              options={groupTypeOptions}
              value={filterState.groupType}
              onChange={(name, option) => {
                setFilterState({ groupType: option.value });
              }}
              onKeyDown={(e) => handleKeyDown(e)}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <GuiButton
                className="btn-outline"
                onClick={() => {
                  setFilterState({
                    locationId: null,
                    groupType: null,
                    filteredGroups: groupOptions,
                  });
                  updateState({ addToGroupId: null });
                }}
              >
                Clear
              </GuiButton>
              <GuiButton
                onClick={() => {
                  const filtered = groupOptions.filter(
                    (g) =>
                      (!filterState.locationId ||
                        g.locationId === filterState.locationId) &&
                      (!filterState.groupType ||
                        g.groupType === filterState.groupType),
                  );
                  setFilterState({ filteredGroups: filtered });
                  updateState({ addToGroupId: null });
                }}
              >
                Apply
              </GuiButton>
            </div>

            <GuiSelect
              label="Group"
              name="addToGroupId"
              isSearchable="true"
              options={
                filterState.locationId || filterState.groupType
                  ? filterState.filteredGroups
                  : groupOptions
              }
              value={state.addToGroupId}
              onChange={(name, option) => updateState(name, option.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="buttons">
            <GuiButton className="btn-outline" onClick={closeModal}>
              Cancel
            </GuiButton>
            <GuiButton
              onClick={addUsersToGroup}
              disabled={!state.addToGroupId}
              loading={state.saving}
            >
              Add {state.individual ? "" : Object.keys(checkboxState).length}{" "}
              User
              {state.individual || Object.keys(checkboxState).length === 1
                ? ""
                : "s"}
            </GuiButton>
          </div>
        </Modal>
      )}

      {/* Delete */}
      {state.showDeleteUsersModal && (
        <Modal
          className="modal bulk-delete-modal"
          showModal={state?.showDeleteUsersModal}
          onClose={() =>
            updateState({ showDeleteUsersModal: false, individual: null })
          }
        >
          <div className="bulk-delete-modal-header">
            <span>Archive User(s)?</span>
            <div
              onClick={(e) =>
                updateState({
                  showDeleteUsersModal: false,
                })
              }
            >
              <i className="fa fa-close close-primary-icon cursor-pointer"></i>
            </div>
          </div>
          <div className="modal-footer-hr-divider"></div>
          <div className="bulk-delete-modal-content ">
            <span>
              Are you sure you want to archive {getUsersText()}? They can be
              restored later from the Archived People page.
            </span>
          </div>
          <div className="bulk-delete-modal-footer">
            <GuiButton
              className="btn-outline"
              onClick={() =>
                updateState({ showDeleteUsersModal: false, individual: null })
              }
            >
              Cancel
            </GuiButton>
            <GuiButton
              className="warning"
              onClick={deleteUsers}
              loading={state.saving}
            >
              Archive{" "}
              {state.individual ? "" : Object.keys(checkboxState).length} User
              {state.individual || Object.keys(checkboxState).length === 1
                ? ""
                : "s"}
            </GuiButton>
          </div>
        </Modal>
      )}

      {/* High Priority */}
      {state.showMakeHighPriority && (
        <Modal
          className="plain-modal"
          showModal={state.showMakeHighPriority}
          onClose={() =>
            updateState({ showMakeHighPriority: false, individual: null })
          }
        >
          <p>Would you like to make {getUsersText()} high priority?</p>
          <div className="buttons">
            <GuiButton
              className="btn-outline"
              onClick={(e) =>
                updateState({ showMakeHighPriority: false, individual: null })
              }
            >
              Cancel
            </GuiButton>
            <GuiButton
              onClick={(e) => setUserPriority(true)}
              loading={state.saving}
            >
              Make High Priority
            </GuiButton>
          </div>
        </Modal>
      )}

      {/* Low Priority */}
      {state.showRemoveHighPriority && (
        <Modal
          className="plain-modal"
          showModal={state.showRemoveHighPriority}
          onClose={() =>
            updateState({ showRemoveHighPriority: false, individual: null })
          }
        >
          <p>
            Are you sure you want to remove high priority from {getUsersText()}?
          </p>
          <div className="buttons">
            <GuiButton
              className="btn-outline"
              onClick={(e) =>
                updateState({ showRemoveHighPriority: false, individual: null })
              }
            >
              Cancel
            </GuiButton>
            <GuiButton
              onClick={(e) => setUserPriority(false)}
              loading={state.saving}
            >
              Remove High Priority
            </GuiButton>
          </div>
        </Modal>
      )}
    </PageLayout>
  );
}
